import { collectionOf } from 'turf';
import { polygon, multiPolygon, featureCollection, isObject, bbox } from 'turf';
import gridToMatrix from './lib/grid-to-matrix';
import {isoBands} from 'marchingsquares';
import * as turf from 'turf'

/**
 * Takes a grid {@link FeatureCollection} of {@link Point} features with z-values and an array of
 * value breaks and generates filled contour isobands.
 *
 * @name isobands
 * @param {FeatureCollection<Point>} pointGrid input points
 * @param {Array<number>} breaks where to draw contours
 * @param {Object} [options={}] options on output
 * @param {string} [options.zProperty='elevation'] the property name in `points` from which z-values will be pulled
 * @param {Object} [options.commonProperties={}] GeoJSON properties passed to ALL isobands
 * @param {Array<Object>} [options.breaksProperties=[]] GeoJSON properties passed, in order, to the correspondent isoband (order defined by breaks)
 * @returns {FeatureCollection<MultiPolygon>} a FeatureCollection of {@link MultiPolygon} features representing isobands
 */
function isobands(pointGrid, breaks, options) {
    // Optional parameters
    options = options || {};
    if (!isObject(options)) throw new Error('options is invalid');
    var zProperty = options.zProperty || 'elevation';
    var commonProperties = options.commonProperties || {};
    var breaksProperties = options.breaksProperties || [];

    // Validation
    collectionOf(pointGrid, 'Point', 'Input must contain Points');
    if (!breaks) throw new Error('breaks is required');
    if (!Array.isArray(breaks)) throw new Error('breaks is not an Array');
    if (!isObject(commonProperties)) throw new Error('commonProperties is not an Object');
    if (!Array.isArray(breaksProperties)) throw new Error('breaksProperties is not an Array');

    // Isoband methods
    var matrix = gridToMatrix(pointGrid, {zProperty: zProperty, flip: true});
    var contours = createContourLines(matrix, breaks, zProperty);
    contours = rescaleContours(contours, matrix, pointGrid);

    var multipolygons = contours.map(function (contour, index) {
        if (breaksProperties[index] && !isObject(breaksProperties[index])) {
            throw new Error('Each mappedProperty is required to be an Object');
        }
        // collect all properties
        var contourProperties = Object.assign(
            {},
            commonProperties,
            breaksProperties[index]
        );
        contourProperties[zProperty] = contour[zProperty];
        var multiP = multiPolygon(contour.groupedRings, contourProperties);
        return multiP;
    });

    return featureCollection(multipolygons);
}

/**
 * Creates the contours lines (featuresCollection of polygon features) from the 2D data grid
 *
 * Marchingsquares process the grid data as a 3D representation of a function on a 2D plane, therefore it
 * assumes the points (x-y coordinates) are one 'unit' distance. The result of the IsoBands function needs to be
 * rescaled, with turfjs, to the original area and proportions on the map
 *
 * @private
 * @param {Array<Array<number>>} matrix Grid Data
 * @param {Array<number>} breaks Breaks
 * @param {string} [property='elevation'] Property
 * @returns {Array<any>} contours
 */
function createContourLines(matrix, breaks, property) {

    var contours = [];
    for (var i = 1; i < breaks.length; i++) {
        var lowerBand = +breaks[i - 1]; // make sure the breaks value is a number
        var upperBand = +breaks[i];

        var isobandsCoords = isoBands(matrix, lowerBand, upperBand - lowerBand);
        // as per GeoJson rules for creating a Polygon, make sure the first element
        // in the array of LinearRings represents the exterior ring (i.e. biggest area),
        // and any subsequent elements represent interior rings (i.e. smaller area);
        // this avoids rendering issues of the MultiPolygons on the map
        var nestedRings = orderByArea(isobandsCoords);
        var groupedRings = groupNestedRings(nestedRings);
        var obj = {};
        obj['groupedRings'] = groupedRings;
        obj[property] = lowerBand + '-' + upperBand;
        contours.push(obj);
    }
    return contours;
}

/**
 * Transform isobands of 2D grid to polygons for the map
 *
 * @private
 * @param {Array<any>} contours Contours
 * @param {Array<Array<number>>} matrix Grid Data
 * @param {Object} points Points by Latitude
 * @returns {Array<any>} contours
 */
function rescaleContours(contours, matrix, points) {

    // get dimensions (on the map) of the original grid
    var gridBbox = turf.bbox(points); // [ minX, minY, maxX, maxY ]
    var originalWidth = gridBbox[2] - gridBbox[0];
    var originalHeigth = gridBbox[3] - gridBbox[1];

    // get origin, which is the first point of the last row on the rectangular data on the map
    var x0 = gridBbox[0];
    var y0 = gridBbox[1];
    // get number of cells per side
    var matrixWidth = matrix[0].length - 1;
    var matrixHeight = matrix.length - 1;
    // calculate the scaling factor between matrix and rectangular grid on the map
    var scaleX = originalWidth / matrixWidth;
    var scaleY = originalHeigth / matrixHeight;

    // resize and shift each point/line of the isobands
    return contours.map(function (contour) {
        contour.groupedRings = contour.groupedRings.map(function (lineRingSet) {
            return lineRingSet.map(function (lineRing) {
                return lineRing.map(point => [point[0] * scaleX + x0, point[1] * scaleY + y0]);
            });
        });
        return contour
    });
}


/*  utility functions */


/**
 * Returns an array of coordinates (of LinearRings) in descending order by area
 *
 * @private
 * @param {Array<LineString>} ringsCoords array of closed LineString
 * @returns {Array} array of the input LineString ordered by area
 */
function orderByArea(ringsCoords) {
    const ringsWithArea = ringsCoords.map(function (coords) {
        // associate each lineRing with its area
        return {ring: coords, area: turf.area(polygon([coords]))};
    });
    ringsWithArea.sort(function (a, b) { // bigger --> smaller
        return b.area - a.area;
    });
    // create a new array of linearRings coordinates ordered by their area
    return ringsWithArea.map(function (x) {
        return x.ring;
    });
}

/**
 * Returns an array of arrays of coordinates, each representing
 * a set of (coordinates of) nested LinearRings,
 * i.e. the first ring contains all the others
 *
 * @private
 * @param {Array} orderedLinearRings array of coordinates (of LinearRings) in descending order by area
 * @returns {Array<Array>} Array of coordinates of nested LinearRings
 */
function groupNestedRings(orderedLinearRings) {
    const orderedLinearRingsWithBBox = orderedLinearRings.map(ring => {
        const poly = polygon([ring]);
        return {
            ring,
            poly,
            BBox: bbox(poly),
        }
    });
    const groupedLinearRingsCoords = [];
    orderedLinearRingsWithBBox.forEach(lf => {
        for (let g = 0; g < groupedLinearRingsCoords.length; g++) {
            const group = groupedLinearRingsCoords[g];
            if (isIn(lf, group[0]) && group.every((hole, i) => i === 0 || !isIn(lf, hole))) {
                group.push(lf);
                return
            }
        }
        groupedLinearRingsCoords.push([lf]);
    });
    return groupedLinearRingsCoords.map(lg => {
        return lg.map(lf => lf.ring)
    });
}

function isIn(test, target) {
    return bBoxInside(test.BBox, target.BBox) && isInside(test.poly, target.poly)
}

/**
 * @private
 * @param {Polygon} testPolygon polygon of interest
 * @param {Polygon} targetPolygon polygon you want to compare with
 * @returns {boolean} true if test-Polygon is inside target-Polygon
 */
function isInside(testPolygon, targetPolygon) {
    var points = turf.explode(testPolygon);
    // polygons in marching squares are ether entirely in or out
    // so if one point is in than they will all be in, and vise versa
     if (points.features.length > 1) {
        if (!turf.booleanPointInPolygon(points.features[0], targetPolygon)) {
            return false;
        }
    }
    return true;
}

/**
 * @private
 * @param {BBox} testBBox BBox of interest
 * @param {BBox} targetBBox BBox you want to compare with
 * @returns {boolean} true if test-BBox is inside target-BBox
 */
function bBoxInside(testBBox, targetBBox) {
    return testBBox[0] >= targetBBox[0] && testBBox[1] >= targetBBox[1] && testBBox[2] <= targetBBox[2] && testBBox[3] <= targetBBox[3]
}

export default isobands;
